// Admin > Messaging Templates: WhatsApp, SMS and email templates, and their connections.
// WhatsApp templates sync both ways with Meta (submit for approval / pull statuses); SMS templates
// come from the DLT portal's export; email templates live here and go out through SMTP.
import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Chip, Button, TextField, MenuItem, Alert, Grid, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tabs, Tab, Tooltip,
  Switch, FormControlLabel, LinearProgress, Divider, Link,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  MESSAGE_TEMPLATES_QUERY, MESSAGING_SETUP_QUERY, SAVE_MESSAGE_TEMPLATE_MUTATION, DELETE_MESSAGE_TEMPLATE_MUTATION,
  SYNC_WHATSAPP_TEMPLATES_MUTATION, SUBMIT_WHATSAPP_TEMPLATE_MUTATION, SAVE_WHATSAPP_BUSINESS_ACCOUNT_MUTATION,
  IMPORT_DLT_TEMPLATES_MUTATION, SAVE_SMS_SETTINGS_MUTATION, CHECK_MESSAGING_CONNECTION_MUTATION, TEST_MESSAGE_TEMPLATE_MUTATION,
} from '../graphql/queries';
import WhatsAppSettingsCard from '../components/WhatsAppSettingsCard';

const CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon sx={{ color: '#25D366' }} /> },
  { key: 'sms', label: 'SMS', icon: <SmsIcon color="primary" /> },
  { key: 'email', label: 'Email', icon: <EmailIcon color="secondary" /> },
];
const STATUS_COLOR = { approved: 'success', pending: 'warning', rejected: 'error', paused: 'warning', disabled: 'default', deleted: 'default', draft: 'info' };
const STATUS_LABEL = { approved: 'Approved', pending: 'In review', rejected: 'Rejected', paused: 'Paused', disabled: 'Disabled', deleted: 'Deleted on provider', draft: 'Draft (only here)' };
const SMS_VAR_RE = /\{#(var|url|urlott|cbn|email|numeric|alphanumeric)#\}/gi;
const SMS_VAR_MAX = { var: 30, numeric: 30, alphanumeric: 30, cbn: 30, email: 100, url: 100, urlott: 100 };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const readFile = (file) => file.text();

// ── Preview (same rules as the server's render) ───────────────────────────────────
function renderPreview(t, fields) {
  const sample = Object.fromEntries(fields.map(f => [f.key, f.sample]));
  const val = (field) => (field ? (sample[field] ?? '') : '');
  if (t.channel === 'whatsapp') {
    const byKey = Object.fromEntries((t.variables || []).map(v => [v.key, val(v.field) || v.sample || `{{${v.key}}}`]));
    return { body: String(t.body || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (m, k) => byKey[k] ?? m) };
  }
  if (t.channel === 'sms') {
    let i = 0; const warnings = [];
    const text = String(t.body || '').replace(SMS_VAR_RE, (m, type) => {
      const v = t.variables?.[i] ? val(t.variables[i].field) : ''; i++;
      const max = SMS_VAR_MAX[type.toLowerCase()];
      if (v.length > max) warnings.push(`Variable ${i} (${v.length} characters) is longer than a {#${type}#} allows (${max})${type.toLowerCase() === 'var' && /^https?:/.test(v) ? ': use {#url#} for links' : ''}`);
      return v || m;
    });
    // GSM-7 messages: 160 characters, or 153 per part when longer; Unicode (e.g. Tamil): 70 / 67
    const unicode = /[^\x00-\x7F₹]/.test(text);
    const per = unicode ? (text.length > 70 ? 67 : 70) : (text.length > 160 ? 153 : 160);
    return { text, warnings, parts: Math.max(1, Math.ceil(text.length / per)), unicode };
  }
  const fill = (s) => String(s || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => sample[k] ?? '');
  return { subject: fill(t.subject), html: fill(t.body) };
}

function WhatsAppBubble({ t, preview }) {
  return (
    <Box sx={{ bgcolor: '#e5ddd5', p: 2, borderRadius: 2 }}>
      <Paper sx={{ p: 1.5, maxWidth: 340, borderRadius: 2, borderTopLeftRadius: 0 }}>
        {t.headerFormat && <Box sx={{ bgcolor: '#ccc', height: 120, borderRadius: 1, mb: 1, display: 'grid', placeItems: 'center' }}><Typography variant="caption">{t.headerFormat} header</Typography></Box>}
        {t.headerText && <Typography fontWeight={700} mb={0.5}>{t.headerText}</Typography>}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{preview.body}</Typography>
        {t.footerText && <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{t.footerText}</Typography>}
        {(t.buttons || []).map((b, i) => (
          <Box key={i} sx={{ borderTop: '1px solid #eee', mt: 1, pt: 1, textAlign: 'center', color: '#00a5f4', fontSize: 14 }}>{b.text || 'Button'}</Box>
        ))}
      </Paper>
    </Box>
  );
}

// ── Editor ──────────────────────────────────────────────────────────────────────
const EMPTY = {
  whatsapp: { channel: 'whatsapp', name: '', language: 'en', category: 'MARKETING', purpose: 'vendor_invite', headerText: '', body: '', footerText: '', buttons: [], variables: [], isActive: true },
  sms: { channel: 'sms', name: '', language: 'en', category: 'promotional', purpose: 'vendor_invite', body: '', variables: [], dltTemplateId: '', providerTemplateId: '', senderId: '', status: 'draft', isActive: true },
  email: { channel: 'email', name: '', language: 'en', purpose: 'vendor_invite', subject: '', body: '', variables: [], status: 'approved', isActive: true },
};

// Placeholders in the body → the mapping rows (keeps the fields already chosen)
function syncVariables(t) {
  let keys = [];
  if (t.channel === 'whatsapp') keys = [...new Set([...String(t.body).matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map(m => Number(m[1])))].sort((a, b) => a - b).map(String);
  if (t.channel === 'sms') keys = (String(t.body).match(SMS_VAR_RE) || []).map((_, i) => String(i + 1));
  const prev = new Map((t.variables || []).map(v => [String(v.key), v]));
  return keys.map(k => prev.get(k) || { key: k, field: '', sample: '' });
}

function TemplateEditor({ initial, setup, onClose, onSaved }) {
  const [t, setT] = useState(() => ({ ...initial, variables: syncVariables(initial) }));
  const [save, { loading, error }] = useMutation(SAVE_MESSAGE_TEMPLATE_MUTATION);
  const bodyRef = useRef();
  const purpose = setup.purposes.find(p => p.key === t.purpose) || setup.purposes[0];
  const fields = purpose?.fields || [];
  const set = (k, v) => setT(x => { const n = { ...x, [k]: v }; return k === 'body' ? { ...n, variables: syncVariables(n) } : n; });
  const setVar = (key, field) => setT(x => ({ ...x, variables: x.variables.map(v => (v.key === key ? { ...v, field, sample: fields.find(f => f.key === field)?.sample || '' } : v)) }));
  const insert = (token) => {
    const el = bodyRef.current;
    const pos = el ? el.selectionStart : t.body.length;
    set('body', `${t.body.slice(0, pos)}${token}${t.body.slice(pos)}`);
  };
  const nextWa = () => `{{${(t.variables || []).length + 1}}}`;
  const preview = renderPreview(t, fields);
  const locked = t.channel === 'whatsapp' && t.providerId && t.status !== 'deleted';
  const setButton = (i, patch) => set('buttons', t.buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  const go = async () => {
    const input = {
      id: t.id, channel: t.channel, name: t.name, language: t.language, category: t.category, purpose: t.purpose, status: t.status,
      subject: t.subject, headerText: t.headerText, body: t.body, footerText: t.footerText, buttons: t.buttons,
      variables: t.variables.map(({ key, field, sample }) => ({ key, field: field || null, sample })),
      dltTemplateId: t.dltTemplateId, providerTemplateId: t.providerTemplateId, senderId: t.senderId, isActive: t.isActive,
    };
    const { data } = await save({ variables: { input } });
    onSaved(data.saveMessageTemplate);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t.id ? 'Edit' : 'New'} {CHANNELS.find(c => c.key === t.channel).label} template</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Stack spacing={2}>
              {locked && <Alert severity="info">This template is on Meta. Its name and language cannot change; edits are kept here until you submit them for review again.</Alert>}
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Name *" fullWidth size="small" value={t.name} disabled={locked}
                    onChange={e => set('name', t.channel === 'whatsapp' ? e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') : e.target.value)}
                    helperText={t.channel === 'whatsapp' ? 'lowercase_with_underscores' : ''} />
                </Grid>
                <Grid item xs={6} sm={3}><TextField label="Language" fullWidth size="small" value={t.language} disabled={locked} onChange={e => set('language', e.target.value)} helperText="en, en_US, ta, hi…" /></Grid>
                {t.channel !== 'email' && (
                  <Grid item xs={6} sm={3}>
                    <TextField select label={t.channel === 'sms' ? 'DLT type' : 'Category'} fullWidth size="small" value={t.category || ''} onChange={e => set('category', e.target.value)}>
                      {(t.channel === 'whatsapp' ? setup.whatsappCategories : setup.smsCategories).map(c => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField select label="Used for" fullWidth size="small" value={t.purpose} onChange={e => set('purpose', e.target.value)}>
                    {setup.purposes.map(p => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                  </TextField>
                </Grid>
                {t.channel !== 'whatsapp' && (
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Status" fullWidth size="small" value={t.status} onChange={e => set('status', e.target.value)}
                      helperText={t.channel === 'sms' ? 'Approved = approved on the DLT portal' : ''}>
                      {(t.channel === 'sms' ? ['draft', 'pending', 'approved', 'rejected', 'disabled'] : ['approved', 'disabled']).map(s => (
                        <MenuItem key={s} value={s}>{t.channel === 'email' && s === 'approved' ? 'Active' : STATUS_LABEL[s]}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
              </Grid>

              {t.channel === 'email' && <TextField label="Subject *" size="small" value={t.subject || ''} onChange={e => set('subject', e.target.value)} />}
              {t.channel === 'whatsapp' && <TextField label="Header (optional, text)" size="small" value={t.headerText || ''} onChange={e => set('headerText', e.target.value)} inputProps={{ maxLength: 60 }} helperText={t.headerFormat ? `This template has a ${t.headerFormat} header on Meta; it is kept as is` : 'Up to 60 characters'} />}
              <Box>
                <TextField inputRef={bodyRef} label={t.channel === 'email' ? 'Body (HTML) *' : 'Message *'} multiline minRows={t.channel === 'email' ? 10 : 5} fullWidth
                  value={t.body} onChange={e => set('body', e.target.value)}
                  helperText={t.channel === 'whatsapp' ? `${t.body.length}/1024 · placeholders {{1}}, {{2}}… in order; not at the very start or end`
                    : t.channel === 'sms' ? 'Must match the DLT-approved text exactly. Placeholders: {#var#} (up to 30 characters), {#url#} for links, {#numeric#}…'
                      : 'Use {{field}} placeholders, e.g. {{name}}'} />
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.5}>
                  {t.channel === 'whatsapp' && <Button size="small" onClick={() => insert(nextWa())}>+ Insert {nextWa()}</Button>}
                  {t.channel === 'sms' && ['{#var#}', '{#url#}', '{#numeric#}'].map(v => <Button key={v} size="small" onClick={() => insert(v)}>+ {v}</Button>)}
                  {t.channel === 'email' && fields.map(f => <Chip key={f.key} size="small" label={`{{${f.key}}}`} onClick={() => insert(`{{${f.key}}}`)} />)}
                </Stack>
              </Box>
              {t.channel === 'whatsapp' && <TextField label="Footer (optional)" size="small" value={t.footerText || ''} onChange={e => set('footerText', e.target.value)} inputProps={{ maxLength: 60 }} />}

              {t.variables.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" mb={1}>What fills each placeholder</Typography>
                  {t.variables.map((v, i) => (
                    <Stack key={v.key} direction="row" spacing={1} alignItems="center" mb={1}>
                      <Chip size="small" label={t.channel === 'whatsapp' ? `{{${v.key}}}` : `Variable ${i + 1}`} sx={{ minWidth: 90 }} />
                      <TextField select size="small" fullWidth value={v.field || ''} onChange={e => setVar(v.key, e.target.value)} error={!v.field} label="App field">
                        {fields.map(f => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
                      </TextField>
                    </Stack>
                  ))}
                  {t.channel === 'whatsapp' && <Typography variant="caption" color="text.secondary">The sample values go to Meta as examples for the review.</Typography>}
                </Paper>
              )}

              {t.channel === 'whatsapp' && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">Buttons</Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" onClick={() => set('buttons', [...t.buttons, { type: 'URL', text: '', url: `{{app_url}}/`, field: null }])}>+ Link</Button>
                      <Button size="small" onClick={() => set('buttons', [...t.buttons, { type: 'QUICK_REPLY', text: '' }])}>+ Quick reply</Button>
                      <Button size="small" onClick={() => set('buttons', [...t.buttons, { type: 'PHONE_NUMBER', text: '', phone: '' }])}>+ Call</Button>
                    </Stack>
                  </Stack>
                  {t.buttons.map((b, i) => (
                    <Grid container spacing={1} key={i} mb={1} alignItems="center">
                      <Grid item xs={3}><Chip size="small" label={b.type.replace('_', ' ').toLowerCase()} /></Grid>
                      <Grid item xs={b.type === 'QUICK_REPLY' ? 8 : 4}><TextField size="small" fullWidth label="Text" value={b.text} onChange={e => setButton(i, { text: e.target.value })} inputProps={{ maxLength: 25 }} /></Grid>
                      {b.type === 'URL' && (
                        <Grid item xs={4}>
                          <TextField size="small" fullWidth label="Link" value={b.url || ''} onChange={e => setButton(i, { url: e.target.value })} helperText="End with {{1}} for a per-recipient part" />
                        </Grid>
                      )}
                      {b.type === 'PHONE_NUMBER' && <Grid item xs={4}><TextField size="small" fullWidth label="Phone" value={b.phone || ''} onChange={e => setButton(i, { phone: e.target.value })} placeholder="+919840000000" /></Grid>}
                      <Grid item xs={1}><IconButton size="small" onClick={() => set('buttons', t.buttons.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton></Grid>
                      {b.type === 'URL' && /\{\{1\}\}$/.test(b.url || '') && (
                        <Grid item xs={12} sx={{ pl: '25% !important' }}>
                          <TextField select size="small" fullWidth label="{{1}} in the link is" value={b.field || ''} onChange={e => setButton(i, { field: e.target.value })} error={!b.field}>
                            {fields.map(f => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
                          </TextField>
                        </Grid>
                      )}
                    </Grid>
                  ))}
                  {!t.buttons.length && <Typography variant="caption" color="text.secondary">No buttons. Marketing templates usually have a link button and a "Not interested" quick reply.</Typography>}
                </Paper>
              )}

              {t.channel === 'sms' && (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={5}><TextField label="DLT template ID" fullWidth size="small" value={t.dltTemplateId || ''} onChange={e => set('dltTemplateId', e.target.value.replace(/\D/g, ''))} helperText="From the DLT portal (19 digits)" /></Grid>
                  <Grid item xs={6} sm={3}><TextField label="Sender ID (header)" fullWidth size="small" value={t.senderId || ''} onChange={e => set('senderId', e.target.value.toUpperCase())} helperText="6 letters" /></Grid>
                  <Grid item xs={6} sm={4}><TextField label="Gateway template ID" fullWidth size="small" value={t.providerTemplateId || ''} onChange={e => set('providerTemplateId', e.target.value)} helperText="MSG91 Flow ID, if used" /></Grid>
                </Grid>
              )}
              <FormControlLabel control={<Switch checked={t.isActive} onChange={e => set('isActive', e.target.checked)} />} label="In use" />
              {error && <Alert severity="error">{error.message}</Alert>}
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" mb={1}>Preview (sample values)</Typography>
            {t.channel === 'whatsapp' && <WhatsAppBubble t={t} preview={preview} />}
            {t.channel === 'sms' && (
              <>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t.senderId || 'XXXXXX'}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{preview.text}</Typography>
                </Paper>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  {preview.text.length} characters · {preview.parts} SMS part{preview.parts > 1 ? 's' : ''}{preview.unicode ? ' (Unicode)' : ''}
                </Typography>
                {preview.warnings.map(w => <Alert key={w} severity="warning" sx={{ mt: 1 }}>{w}</Alert>)}
              </>
            )}
            {t.channel === 'email' && (
              <Paper variant="outlined">
                <Box sx={{ p: 1, borderBottom: '1px solid #eee' }}><Typography variant="body2"><b>Subject:</b> {preview.subject}</Typography></Box>
                <iframe title="Email preview" sandbox="" srcDoc={preview.html} style={{ width: '100%', height: 420, border: 0 }} />
              </Paper>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={go}>{loading ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// templates: the approved templates to choose from; template: the one to start with (optional)
function TestDialog({ template, templates, channel, onClose }) {
  const [to, setTo] = useState('');
  const [id, setId] = useState(template?.id || templates[0]?.id || '');
  const [run, { data, loading, error, reset }] = useMutation(TEST_MESSAGE_TEMPLATE_MUTATION);
  const chosen = templates.find(t => t.id === id);
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Send a test{chosen ? `: ${chosen.name}` : ''}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Sends one message with sample values to you, through the live connection (the provider may charge for it).
        </Typography>
        <TextField select fullWidth label="Template" value={id} onChange={e => { setId(e.target.value); reset(); }} sx={{ mb: 2 }}
          helperText={templates.length ? 'Only approved templates can be sent' : 'No approved templates yet'}>
          {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name} · {t.language}</MenuItem>)}
        </TextField>
        <TextField autoFocus fullWidth label={channel === 'email' ? 'Your email' : 'Your mobile number'} value={to} onChange={e => setTo(e.target.value)} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}
        {data && <Alert severity="success" sx={{ mt: 2 }}>{data.testMessageTemplate}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<SendIcon />} disabled={!to || !id || loading} onClick={() => run({ variables: { id, to } }).catch(() => {})}>Send test</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Template list per channel ──────────────────────────────────────────────────
function TemplateList({ channel, setup, connections, refetchSetup, notify }) {
  const { data, loading, refetch } = useQuery(MESSAGE_TEMPLATES_QUERY, { variables: { channel }, fetchPolicy: 'cache-and-network' });
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [sync, { loading: syncing }] = useMutation(SYNC_WHATSAPP_TEMPLATES_MUTATION);
  const [submit] = useMutation(SUBMIT_WHATSAPP_TEMPLATE_MUTATION);
  const [remove] = useMutation(DELETE_MESSAGE_TEMPLATE_MUTATION);
  const [importDlt, { loading: importing }] = useMutation(IMPORT_DLT_TEMPLATES_MUTATION);
  const file = useRef();
  const rows = data?.messageTemplates || [];
  const purposeLabel = Object.fromEntries(setup.purposes.map(p => [p.key, p.label]));
  const wa = connections.whatsapp;
  const act = async (fn, ok) => {
    try { const r = await fn(); notify('success', typeof ok === 'function' ? ok(r) : ok); refetch(); refetchSetup(); } catch (e) { notify('error', e.message); }
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} mb={2}>
        <Box sx={{ flex: 1 }}>
          {channel === 'whatsapp' && (
            <Typography variant="body2" color="text.secondary">
              {wa.templateSync ? <>Synced with Meta {wa.lastSyncedAt ? `· last ${fmt(wa.lastSyncedAt)}` : ''} (statuses refresh every 6 hours).</> : 'Connect WhatsApp (Connections tab) to sync templates with Meta.'}
              {wa.lastSyncError && <Typography component="span" variant="body2" color="error"> Last sync failed: {wa.lastSyncError}</Typography>}
            </Typography>
          )}
          {channel === 'sms' && <Typography variant="body2" color="text.secondary">DLT templates are approved on your operator's DLT portal. Export them there (Excel/CSV) and import the file here to sync IDs and statuses.</Typography>}
          {channel === 'email' && <Typography variant="body2" color="text.secondary">Email templates are sent through your SMTP server (Email Settings). They need no outside approval.</Typography>}
        </Box>
        {channel === 'whatsapp' && (
          <Button variant="outlined" startIcon={<SyncIcon />} disabled={!wa.templateSync || syncing}
            onClick={() => act(() => sync(), r => { const s = r.data.syncWhatsAppTemplates; return `Synced ${s.onMeta} template(s) from Meta: ${s.created} new, ${s.updated} updated, ${s.removedOnMeta} deleted on Meta.`; })}>
            {syncing ? 'Syncing…' : 'Sync from Meta'}
          </Button>
        )}
        {channel === 'sms' && (
          <>
            <input ref={file} type="file" accept=".csv,text/csv" hidden onChange={async e => {
              const f = e.target.files?.[0]; e.target.value = '';
              if (f) act(async () => importDlt({ variables: { csv: await readFile(f) } }), r => { const s = r.data.importDltTemplates; return `DLT import: ${s.created} new, ${s.updated} updated, ${s.skipped} rows skipped.`; });
            }} />
            <Button variant="outlined" startIcon={<UploadFileIcon />} disabled={importing} onClick={() => file.current?.click()}>Import DLT export (CSV)</Button>
          </>
        )}
        <Button variant="outlined" startIcon={<SendIcon />} disabled={!rows.some(t => t.sendable)} onClick={() => setTesting({})}>Send a test</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ ...EMPTY[channel] })}>New template</Button>
      </Stack>

      <Paper variant="outlined">
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Template</TableCell>
                <TableCell>Used for</TableCell>
                <TableCell>{channel === 'sms' ? 'DLT' : channel === 'whatsapp' ? 'Category' : 'Subject'}</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(t => {
                const unmapped = (t.variables || []).some(v => !v.field) || (t.buttons || []).some(b => b.type === 'URL' && /\{\{1\}\}$/.test(b.url || '') && !b.field);
                // Templates not in use are not submitted (e.g. ones replaced by a new template)
                const canSubmit = channel === 'whatsapp' && t.isActive && (!t.providerId || t.hasLocalChanges || ['rejected', 'deleted'].includes(t.status));
                return (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 420 }}>
                        {t.language} · {channel === 'email' ? t.body.replace(/<[^>]+>/g, ' ') : t.body}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{purposeLabel[t.purpose] || t.purpose}</Typography></TableCell>
                    <TableCell>
                      {channel === 'sms' ? <Typography variant="caption">{t.dltTemplateId || 'No DLT ID'}{t.senderId ? ` · ${t.senderId}` : ''}<br />{t.category}</Typography>
                        : channel === 'whatsapp' ? <Typography variant="caption">{t.category}{t.qualityScore ? ` · quality ${t.qualityScore}` : ''}</Typography>
                          : <Typography variant="caption">{t.subject}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Chip size="small" label={channel === 'email' && t.status === 'approved' ? 'Active' : (STATUS_LABEL[t.status] || t.status)} color={STATUS_COLOR[t.status] || 'default'} icon={t.sendable ? <CheckCircleIcon /> : undefined} />
                        {!t.isActive && <Chip size="small" variant="outlined" label="Not in use" />}
                        {t.hasLocalChanges && <Chip size="small" variant="outlined" color="warning" label="Edited, not submitted" />}
                        {unmapped && <Chip size="small" variant="outlined" color="error" label="Map placeholders" />}
                        {t.rejectedReason && <Typography variant="caption" color="error">{t.rejectedReason}</Typography>}
                        {t.lastError && <Typography variant="caption" color="error">{t.lastError}</Typography>}
                        {t.syncedAt && <Typography variant="caption" color="text.secondary">synced {fmt(t.syncedAt)}</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {canSubmit && (
                        <Tooltip title={wa.templateSync ? (t.providerId ? 'Submit the edits to Meta for review' : 'Submit to Meta for approval') : 'Connect WhatsApp first'}>
                          <span>
                            <IconButton size="small" color="primary" disabled={!wa.templateSync}
                              onClick={() => act(() => submit({ variables: { id: t.id } }), 'Submitted to Meta. Approval usually takes from a few minutes to 24 hours.')}><CloudUploadIcon fontSize="small" /></IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title={t.sendable ? 'Send a test' : 'Only approved templates can be sent'}>
                        <span><IconButton size="small" disabled={!t.sendable} onClick={() => setTesting(t)}><SendIcon fontSize="small" /></IconButton></span>
                      </Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={t.providerId && t.status !== 'deleted' ? 'Delete here and on Meta' : 'Delete'}>
                        <IconButton size="small" onClick={() => {
                          const msg = t.providerId && t.status !== 'deleted'
                            ? `Delete "${t.name}" here and on Meta? Meta does not allow the name to be reused for 30 days.` : `Delete "${t.name}"?`;
                          if (window.confirm(msg)) act(() => remove({ variables: { id: t.id } }), 'Template deleted.');
                        }}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && !rows.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>No templates yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {editing && (
        <TemplateEditor initial={editing} setup={setup} onClose={() => setEditing(null)}
          onSaved={(t) => { setEditing(null); refetch(); notify('success', channel === 'whatsapp' && (!t.providerId || t.hasLocalChanges) ? `Saved "${t.name}". Submit it to Meta for approval when ready.` : `Saved "${t.name}".`); }} />
      )}
      {testing && <TestDialog template={testing.id ? testing : null} templates={rows.filter(t => t.sendable)} channel={channel} onClose={() => setTesting(null)} />}
    </>
  );
}

// ── Connections ────────────────────────────────────────────────────────────────
function ConnectionCheck({ channel }) {
  const [run, { data, loading, error }] = useMutation(CHECK_MESSAGING_CONNECTION_MUTATION);
  return (
    <Box>
      <Button size="small" variant="outlined" disabled={loading} onClick={() => run({ variables: { channel } }).catch(() => {})}>{loading ? 'Checking…' : 'Check connection'}</Button>
      {data && <Alert severity="success" sx={{ mt: 1 }}>{data.checkMessagingConnection}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error.message}</Alert>}
    </Box>
  );
}

const SMS_PLACEHOLDERS = '{{to}} (91XXXXXXXXXX) · {{to10}} · {{text}} · {{dlt_template_id}} · {{provider_template_id}} · {{sender_id}} · {{entity_id}} · {{vars_pipe}} · {{vars_json}} · {{api_key}}';

function SmsSettingsCard({ sms, onSaved }) {
  const [f, setF] = useState(() => ({
    provider: sms.provider, isActive: sms.isActive, senderId: sms.senderId, entityId: sms.entityId, apiKey: '',
    httpUrl: sms.httpUrl, httpMethod: sms.httpMethod, httpHeaders: JSON.stringify(sms.httpHeaders || {}, null, 0), httpBody: sms.httpBody,
  }));
  const [save, { loading, error, data }] = useMutation(SAVE_SMS_SETTINGS_MUTATION);
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <SmsIcon color="primary" /><Typography variant="h6" fontWeight={600}>SMS gateway</Typography>
        <Chip size="small" color={sms.isActive && sms.configured ? 'success' : 'default'} label={sms.isActive && sms.configured ? 'On' : 'Off'} />
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>
        In India, business SMS must be registered on DLT (TRAI): your company (Principal Entity ID), a 6-letter sender ID (header),
        and every message text as a template. Promotional SMS reach only numbers not on the DND list, 10 am to 9 pm.
      </Alert>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4}>
          <TextField select fullWidth size="small" label="Provider" value={f.provider} onChange={set('provider')}>
            <MenuItem value="msg91">MSG91 (Flow API)</MenuItem>
            <MenuItem value="custom_http">Other gateway (HTTP request)</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Default sender ID" value={f.senderId} onChange={e => setF(x => ({ ...x, senderId: e.target.value.toUpperCase() }))} /></Grid>
        <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="DLT Principal Entity ID" value={f.entityId} onChange={set('entityId')} /></Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" type="password" label={f.provider === 'msg91' ? 'MSG91 auth key' : 'API key (used as {{api_key}})'} value={f.apiKey} onChange={set('apiKey')}
            helperText={sms.hasApiKey ? 'A key is saved: leave empty to keep it' : ''} />
        </Grid>
        {f.provider === 'custom_http' && (
          <>
            <Grid item xs={9}><TextField fullWidth size="small" label="Request URL" value={f.httpUrl} onChange={set('httpUrl')} placeholder="https://gateway.example.com/send?apikey={{api_key}}" /></Grid>
            <Grid item xs={3}>
              <TextField select fullWidth size="small" label="Method" value={f.httpMethod} onChange={set('httpMethod')}>
                <MenuItem value="POST">POST</MenuItem><MenuItem value="GET">GET</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Headers (JSON)" value={f.httpHeaders} onChange={set('httpHeaders')} placeholder='{"Authorization": "Bearer {{api_key}}"}' /></Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline minRows={3} label="Body" value={f.httpBody} onChange={set('httpBody')}
                placeholder='{"sender": "{{sender_id}}", "to": "{{to10}}", "message": "{{text}}", "template_id": "{{dlt_template_id}}"}'
                helperText={`Placeholders: ${SMS_PLACEHOLDERS}. Use your gateway's DLT API documentation.`} />
            </Grid>
          </>
        )}
        {f.provider === 'msg91' && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Create each DLT template in MSG91 as a Flow, then enter its MSG91 template ID on the template here (Gateway template ID). The variables are sent as var1, var2… in order.
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <FormControlLabel control={<Switch checked={f.isActive} onChange={e => setF(x => ({ ...x, isActive: e.target.checked }))} />} label="Use this gateway to send SMS" />
        </Grid>
      </Grid>
      {error && <Alert severity="error" sx={{ my: 1 }}>{error.message}</Alert>}
      {data && <Alert severity="success" sx={{ my: 1 }}>SMS settings saved.</Alert>}
      <Stack direction="row" spacing={1} mt={1}>
        <Button variant="contained" disabled={loading} onClick={async () => { await save({ variables: { input: f } }).catch(() => {}); onSaved(); }}>Save</Button>
        <ConnectionCheck channel="sms" />
      </Stack>
      {sms.lastTestedAt && <Typography variant="caption" color="text.secondary" display="block" mt={1}>Last test {fmt(sms.lastTestedAt)}{sms.lastError ? `: ${sms.lastError}` : ': sent'}</Typography>}
    </Paper>
  );
}

function Connections({ connections, refetch }) {
  const [waba, setWaba] = useState(connections.whatsapp.wabaId || '');
  const [saveWaba, { loading, error, data }] = useMutation(SAVE_WHATSAPP_BUSINESS_ACCOUNT_MUTATION);
  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <WhatsAppIcon sx={{ color: '#25D366' }} /><Typography variant="h6" fontWeight={600}>WhatsApp template sync (Meta)</Typography>
          <Chip size="small" color={connections.whatsapp.templateSync ? 'success' : 'default'} label={connections.whatsapp.templateSync ? 'Sync ready' : 'Not connected'} />
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Templates belong to your WhatsApp Business Account. Find its ID in Meta WhatsApp Manager → Account tools / API setup. The access token
          (below, in WhatsApp settings) needs the <b>whatsapp_business_management</b> permission to sync templates, and <b>whatsapp_business_messaging</b> to send.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField size="small" label="WhatsApp Business Account ID" value={waba} onChange={e => setWaba(e.target.value.replace(/\D/g, ''))} sx={{ minWidth: 280 }} />
          <Button variant="contained" disabled={loading} onClick={async () => { await saveWaba({ variables: { wabaId: waba } }).catch(() => {}); refetch(); }}>Save</Button>
          <ConnectionCheck channel="whatsapp" />
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error.message}</Alert>}
        {data && <Alert severity="success" sx={{ mt: 1 }}>Saved.</Alert>}
      </Paper>
      <WhatsAppSettingsCard />
      <SmsSettingsCard sms={connections.sms} onSaved={refetch} />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <EmailIcon color="secondary" /><Typography variant="h6" fontWeight={600}>Email (SMTP)</Typography>
          <Chip size="small" color={connections.email.configured ? 'success' : 'default'} label={connections.email.configured ? `${connections.email.fromEmail} via ${connections.email.host}` : 'Not set up'} />
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Email goes out through your SMTP server. Set it up in <Link component={RouterLink} to="/dashboard/admin/email-settings">Email Settings</Link>.
        </Typography>
        <ConnectionCheck channel="email" />
      </Paper>
    </Stack>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function AdminMessageTemplatesPage() {
  const [tab, setTab] = useState(0);
  const [notice, setNotice] = useState(null);
  const { data, error, refetch } = useQuery(MESSAGING_SETUP_QUERY, { fetchPolicy: 'cache-and-network' });
  const setup = data?.messagingSetup;
  const notify = (severity, text) => setNotice({ severity, text });
  const status = useMemo(() => {
    if (!setup) return [];
    const c = setup.connections;
    return [
      ['WhatsApp', c.whatsapp.templateSync ? 'synced with Meta' : c.whatsapp.configured ? 'sending only (no account ID)' : 'not connected', c.whatsapp.templateSync],
      ['SMS', c.sms.isActive && c.sms.configured ? 'gateway on' : 'no gateway', c.sms.isActive && c.sms.configured],
      ['Email', c.email.configured ? 'SMTP set up' : 'SMTP not set up', c.email.configured],
    ];
  }, [setup]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700}>Messaging templates</Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        WhatsApp, SMS and email messages the app sends (Marketing invitations, vendor login details…), with the provider's approval status.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
        {status.map(([k, l, ok]) => <Chip key={k} size="small" variant="outlined" color={ok ? 'success' : 'default'} label={`${k}: ${l}`} />)}
      </Stack>
      {error && <Alert severity="error">{error.message}</Alert>}
      {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice.text}</Alert>}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
        {CHANNELS.map(c => <Tab key={c.key} icon={c.icon} iconPosition="start" label={c.label} />)}
        <Tab icon={<SettingsEthernetIcon />} iconPosition="start" label="Connections" />
      </Tabs>
      {!setup && <LinearProgress />}
      {setup && tab < 3 && <TemplateList key={CHANNELS[tab].key} channel={CHANNELS[tab].key} setup={setup} connections={setup.connections} refetchSetup={refetch} notify={notify} />}
      {setup && tab === 3 && <><Divider sx={{ mb: 2 }} /><Connections connections={setup.connections} refetch={refetch} /></>}
    </Box>
  );
}
